export const isPositive = (num: any) => {
  return num !== null && num > 0.0
}

export const isNegative = (num: any) => {
  return num !== null && num < 0.0
}

// value is either a number or a valid number string
export const isNumeric = (num: any) => {
  return !isNaN(num - parseFloat(num))
}

export const sum = (numbers: any) => {
  const _sum = (amount: any, element: any) => amount + element
  return numbers.reduce(_sum, 0)
}

// given an array of objects <elements>, sum all the values for a given property <key>
// example: sumProperty([{a: 1, b: 2}, ...], "b")
export const sumProperty = (elements: any, key: any) => {
  return sum(elements.map((e: any) => Number(e[key])))
}

// turn numeric values (numbers or number strings) into numbers
// return null for any other value
export const toNumberOrNull = (value: any) => {
  return isNumeric(value) ? value * 1 : null
}

// convert string to a number, stripping out currency symbols & commas
export const toRawNumber = (value: any) => {
  if (value !== 0 && !value) return value // falsy non-zero values
  if (!/\d/.test(value.toString())) return value // no number in the string
  return Number(value.toString().replace(/[,$]/g, ""))
}

// --- Currency formatting ---

// Locale-aware currency symbol/prefix. Memoized — Intl.NumberFormat
// construction is non-trivial and a kanban with 100+ cards re-renders
// this on every paint. Symbols are stable for a given ISO code so we
// can cache aggressively.
const _currencySymbolCache = new Map()
export const currencySymbol = (currency = "USD") => {
  const code = typeof currency === "string" && currency ? currency : "USD"
  const cached = _currencySymbolCache.get(code)
  if (cached !== undefined) return cached
  let symbol
  try {
    const parts = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
    }).formatToParts(0)
    symbol = parts.find((p) => p.type === "currency")?.value ?? code
  } catch (err) {
    console.warn("[currencySymbol] unrecognised currency code, falling back to code", code, err)
    symbol = code
  }
  _currencySymbolCache.set(code, symbol)
  return symbol
}

// return a formatted currency string rounding off cents
export const currencyDollars = (value: any, currency = "USD") => {
  if (parseFloat(value) != value) return value
  return currencyFull(Math.round(value), currency)
}

export const currencyFull = (value: any, currency = "USD") => {
  if (parseFloat(value) != value) return value

  const [dollars, cents] = value.toString().split(".")
  const chunks = dollars.match(/.{1,3}(?=(.{3})*$)/g)
  let formatted = currencySymbol(currency) + (chunks as any).join(",")
  if (cents) {
    formatted = formatted + "." + cents
  }
  return formatted
}

export const currencyShort = (
  value: any,
  currency = "USD",
  { negativeParens = false }: { negativeParens?: boolean } = {}
) => {
  if (typeof value !== "number" || isNaN(value)) {
    // Caller passed a non-numeric value (e.g. a string from a malformed
    // sync). Don't try to format — surface the issue, then return the
    // raw value so the page still renders.
    console.warn("[currencyShort] non-numeric value, returning as-is", value)
    return value
  }

  // Always abbreviate the magnitude — the sign never reaches the
  // digit-chunking logic below, so it can't interfere with B/M/k tier
  // detection (or, in the M/B tier, land inside _buildSummaryNumber's
  // Number(summary) + 1 carry-over arithmetic, which assumes a positive
  // string). The sign is applied once, at the end, in whichever style the
  // caller asked for:
  //   default:          "-$1.2M" (sign outside the symbol)
  //   negativeParens:    "($1.2M)" (accounting notation)
  const isNegative = value < 0
  const magnitude = isNegative ? -value : value

  const [dollars] = magnitude.toString().split(".")
  const chunks: any = dollars!.match(/.{1,3}(?=(.{3})*$)/g)
  let formatted: any

  if (chunks.length == 4) {
    formatted = _buildSummaryNumber(chunks) + "B"
  } else if (chunks.length == 3) {
    formatted = _buildSummaryNumber(chunks) + "M"
  } else if (chunks.length == 2) {
    formatted = Math.round(magnitude / 1000.0) + "k"
  } else if (chunks.length == 1) {
    formatted = Math.round(magnitude)
  } else {
    formatted = chunks.join(",")
  }
  const symbolValue = currencySymbol(currency) + formatted
  if (!isNegative) return symbolValue
  return negativeParens ? `(${symbolValue})` : `-${symbolValue}`
}

const _buildSummaryNumber = (chunks: any) => {
  let fragment
  // round from three to two digits
  const partial = Math.round(chunks[1] / 10.0)
  let summary = chunks[0]
  if (partial == 100) {
    // partial rounds to a full summary digit
    summary = (Number(summary) + 1).toString()
  } else if (partial != 0) {
    let partialString = partial.toString()
    if (partialString.length === 1) {
      // add back leading zero
      partialString = "0" + partialString
    }
    // trim trailing zeros
    fragment = partialString[1] === "0" ? partialString[0] : partialString
    summary += "." + fragment
  }
  return summary
}

// --- Display helpers (render "-"/blank for empty; wrap the raw formatters
// above). These coerce with Number()/guard empty, so — unlike the raw
// formatters above — they always return a display string. They carry the
// empty-value sentinels the old $filters facade added. ---

// numeric-ish input: a number, a numeric string, or absent.
export type NumericInput = number | string | null | undefined

// ex: 45.5 -> "45.50%" (input is already a percentage; "-" for empty)
export const formatPercentage = (value: number | null | undefined): string => {
  if (!value) return "-"
  return `${(Math.round(value * 100) / 100).toFixed(2)}%`
}

// full grouped currency; "-" for empty
export const formatCurrency = (value: NumericInput, currency = "USD"): string => {
  if (!value) return "-"
  return currencyFull(Number(value), currency)
}

// rounded currency (no cents); "-" for empty
export const formatCurrencyRounded = (value: NumericInput, currency = "USD"): string => {
  if (!value) return "-"
  return currencyDollars(Number(value), currency)
}

// abbreviated k/M/B currency; <blank> (default "-") for empty
export const formatCurrencyShort = (value: NumericInput, blank = "-", currency = "USD"): string => {
  if (!value && value !== 0) return blank
  return currencyShort(Number(value), currency)
}

// Parse currency text to a number (or null): tolerates a symbol, commas and
// whitespace, and k/M/B shorthand ("50k" -> 50000). Null when nothing parses.
export const parseCurrency = (input: NumericInput): number | null => {
  if (input == null) return null
  const cleaned = String(input)
    .trim()
    .replace(/[$£€,\s]/g, "")
  if (!cleaned) return null
  const match = cleaned.match(/^(-?\d*\.?\d+)([kmb])?$/i)
  if (!match) return null
  const n = Number(match[1])
  if (!Number.isFinite(n)) return null
  const multiplier = { k: 1e3, m: 1e6, b: 1e9 }[match[2]?.toLowerCase() ?? ""] ?? 1
  return n * multiplier
}

// human-readable byte size: 1536 -> "1.50 KB"; "—" for empty/NaN
export const formatFileSize = (bytes: number | null | undefined, decimals = 2): string => {
  if (bytes === null || bytes === undefined || isNaN(bytes)) return "—"
  if (bytes === 0) return "0 B"

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ["B", "KB", "MB", "GB", "TB", "PB"]

  const i = Math.floor(Math.log(bytes) / Math.log(k))
  const value = bytes / Math.pow(k, i)

  return `${value.toFixed(dm)} ${sizes[i]}`
}

/** Seconds as m:ss, or h:mm:ss from an hour up. */
export const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return "—"
  const total = Math.round(seconds)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = String(total % 60).padStart(2, "0")
  return h ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`
}

export default {
  isPositive,
  isNegative,
  isNumeric,
  toNumberOrNull,
  toRawNumber,
  currencySymbol,
  currencyDollars,
  currencyFull,
  currencyShort,
  parseCurrency,
  formatPercentage,
  formatCurrency,
  formatCurrencyRounded,
  formatCurrencyShort,
  formatFileSize,
}
